import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Award, Target, ArrowRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials } from '../mockData';
import usePageMeta from '../hooks/usePageMeta';

const values = [
  { icon: Shield, title: 'Workmanship', text: 'Clean, well-documented installations designed to last — and to minimise callbacks' },
  { icon: Users, title: 'Collaboration', text: 'We work alongside integrators, builders and FM teams — never against them' },
  { icon: Target, title: 'Reliability', text: 'We turn up, deliver to programme, and coordinate with other trades on site' },
  { icon: Award, title: 'Compliance', text: 'NZ building codes, fire safety and IQP requirements handled properly, every job' },
];

// Each person gets their name above their own photos (and videos). Neo's the dog.
const team = [
  {
    name: 'Johan',
    role: 'Apprentice',
    images: ['/images/team/johan-1.jpeg'],
    imageCaptions: ['On the tools'],
  },
  {
    name: 'Sibi',
    role: 'Installer',
    images: ['/images/team/sibi-1.jpeg'],
  },
  {
    name: 'Neo',
    role: 'Mascot',
    images: ['/images/team/neo-1.jpeg', '/images/team/neo-2.jpeg'],
    note: 'Chief morale officer — rides shotgun in the van.',
  },
];

const About = () => {
  usePageMeta();

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 lg:py-24 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/projects/north-shore-hospital.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">About Shore Doors Locksmiths</h1>
            <p className="text-xl sm:text-2xl text-gray-300">
              The installer the professionals already use — now a full-service locksmith for everyone
            </p>
          </div>
        </div>
      </section>

      {/* The story */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Our story, told straight</h2>
              <p className="text-lg text-gray-700 mb-6">
                Shore Doors built its name the unglamorous way: as the installation delivery
                partner that Auckland's access control companies and builders call when the
                hardware has to go in right. Hospitals, banks, schools, libraries, train stations —
                our team has installed and serviced the locks, maglocks, electric strikes, drop
                bolts and door hardware behind them for years.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Today we're a full-service locksmith business, open to homeowners and businesses
                directly — not just the trade. Lockouts, rekeying, key cutting, lock repairs and
                security upgrades, alongside the commercial installation work the industry already
                knows us for.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We're also the only business in Auckland certified to install and service the
                AHS A200 door actuator — automatic door opening that we regularly deliver for
                disabled customers through ACC.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                We're fully mobile — well-equipped vans, no shopfront — covering the North Shore
                for residential work and greater Auckland for commercial. Everything we do carries
                a 12-month workmanship guarantee.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  Work with us
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <figure className="relative m-0">
              <img
                src="/images/team/director-hugh-sonali.jpeg"
                alt="Hugh, Director, and Sonali, Accounts — Shore Doors Locksmiths"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
              <figcaption className="mt-4 text-center text-gray-600">
                <span className="font-semibold text-gray-900">Sonali</span> — Accounts
                <span className="mx-2 text-gray-300">·</span>
                <span className="font-semibold text-gray-900">Hugh</span> — Director
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Meet the team */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Meet the team</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The crew who turn up at your door — and one very good boy.
            </p>
          </div>
          <div className="space-y-14">
            {team.map((person) => (
              <div key={person.name}>
                <div className="mb-5 border-b border-gray-200 pb-3">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="text-2xl font-bold text-gray-900">{person.name}</h3>
                    <span className="text-red-600 font-semibold">{person.role}</span>
                  </div>
                  {person.note && <p className="text-gray-500 text-sm mt-1">{person.note}</p>}
                </div>
                <div className="flex flex-wrap items-start gap-4">
                  {person.images.map((src, i) => {
                    const wide = person.wide && person.wide.includes(i);
                    return (
                      <figure
                        key={src}
                        className={`relative m-0 rounded-xl overflow-hidden shadow-sm ${wide ? 'w-full sm:w-[28rem]' : 'w-40 sm:w-48'}`}
                      >
                        <img
                          src={src}
                          alt={`${person.name} — Shore Doors Locksmiths`}
                          loading="lazy"
                          className={wide ? 'w-full aspect-[16/10] object-contain bg-gray-100' : 'w-full aspect-[4/5] object-cover'}
                        />
                        {person.imageCaptions && person.imageCaptions[i] && (
                          <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent text-white text-xs font-medium px-2 pt-6 pb-2">
                            {person.imageCaptions[i]}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}
                </div>
                {person.videos && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4 max-w-3xl">
                    {person.videos.map((v) => (
                      <video
                        key={v}
                        controls
                        playsInline
                        preload="metadata"
                        src={v}
                        className="w-full h-[360px] object-contain bg-gray-950 rounded-xl"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our core values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <Card key={v.title} className="border-gray-200 text-center">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <v.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{v.title}</h3>
                  <p className="text-gray-600">{v.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* One testimonial + link */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="text-blue-300 mx-auto mb-4" size={36} />
          <blockquote className="text-xl text-gray-700 italic leading-relaxed mb-6">
            "{testimonials[4].quote}"
          </blockquote>
          <p className="font-semibold text-gray-900">{testimonials[4].name}</p>
          <p className="text-gray-500 text-sm mb-8">{testimonials[4].title}, {testimonials[4].company}</p>
          <Link to="/reviews">
            <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8">
              Read all reviews
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
